import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
} from 'react-share';
import CommentSection from '@/components/CommentSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostDetailPage = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  }, [postId]);

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [fetchPost, fetchComments]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-roboto text-lg">Loading...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-roboto text-lg">Post not found</p>
      </div>
    );
  }

  const shareUrl = window.location.href;

  return (
    <div data-testid="blog-post-detail">
      <section className="section-spacing primary-green text-white">
        <div className="content-container">
          <Link to="/blog" className="text-white hover:text-gray-200 mb-4 inline-block">
            ← Back to Blog
          </Link>
          <h1 className="font-rubik text-4xl md:text-5xl font-bold mb-4 uppercase">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm">
            <span className="bg-white text-[#aaae10] px-3 py-1 rounded-full font-roboto font-medium">
              {post.category}
            </span>
            <span>{post.date}</span>
            <span>{post.views || 0} views</span>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="content-container max-w-4xl">
          {post.image_url && (
            <img src={post.image_url} alt={post.title} className="w-full h-auto rounded-lg mb-8" />
          )}
          
          <div className="font-roboto text-lg leading-relaxed mb-8">
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />
          </div>

          <div className="flex items-center gap-4 py-6 border-y mb-8">
            <span className="font-rubik font-semibold text-[#646464]">SHARE THIS POST:</span>
            <div className="flex gap-2">
              <FacebookShareButton url={shareUrl} hashtag="#NDEBusiness">
                <FacebookIcon size={40} round />
              </FacebookShareButton>

              <TwitterShareButton
                url={shareUrl}
                title={post.title}
                hashtags={['NDEBusiness', 'Suriname', 'Sustainability']}
              >
                <TwitterIcon size={40} round />
              </TwitterShareButton>

              <LinkedinShareButton
                url={shareUrl}
                title={post.title}
                summary={post.excerpt}
              >
                <LinkedinIcon size={40} round />
              </LinkedinShareButton>

              <WhatsappShareButton url={shareUrl} title={post.title}>
                <WhatsappIcon size={40} round />
              </WhatsappShareButton>
            </div>
          </div>

          <CommentSection 
            postId={postId} 
            comments={comments}
            onCommentAdded={fetchComments}
          />
        </div>
      </section>
    </div>
  );
};

export default BlogPostDetailPage;
